import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Performance as PerformanceIcon,
  Table as TableIcon
} from "@dreamhub/designsystem/icons";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";

export type ChartTableViewMode = "chart" | "table";

export const VIEW_TAB_ORDER: ChartTableViewMode[] = ["table", "chart"];

const renderViewIcon: Record<ChartTableViewMode, () => JSX.Element> = {
  table: () => <TableIcon />,
  chart: () => <PerformanceIcon />
};

const DEFAULT_TOOLTIP_KEYS: Record<ChartTableViewMode, string> = {
  chart: "chat.tooltips.showAsChart",
  table: "chat.tooltips.showAsTable"
};

interface UseChartTableViewTabOptionsParams {
  disabledMode?: ChartTableViewMode;
  disabledTooltipKey?: string;
}

export const useChartTableViewTabOptions = ({
  disabledMode,
  disabledTooltipKey
}: UseChartTableViewTabOptionsParams = {}) => {
  const { t } = useTranslation();

  return useMemo<Record<ChartTableViewMode, JSX.Element>>(
    () =>
      VIEW_TAB_ORDER.reduce(
        (acc, mode) => {
          const isDisabled = mode === disabledMode;
          const tooltipKey =
            isDisabled && disabledTooltipKey
              ? disabledTooltipKey
              : DEFAULT_TOOLTIP_KEYS[mode];
          const iconElement = renderViewIcon[mode]();
          const triggerContent = isDisabled ? (
            <span className="opacity-40">{iconElement}</span>
          ) : (
            iconElement
          );
          acc[mode] = (
            <Tooltip>
              <TooltipTrigger asChild>{triggerContent}</TooltipTrigger>
              <TooltipContent className="max-w-[180px]">
                {t(tooltipKey)}
              </TooltipContent>
            </Tooltip>
          );
          return acc;
        },
        {} as Record<ChartTableViewMode, JSX.Element>
      ),
    [t, disabledMode, disabledTooltipKey]
  );
};
